import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { 
  Radio, 
  Wifi, 
  Antenna, 
  Smartphone, 
  Monitor, 
  ArrowRight,
  CheckCircle,
  Orbit,
  Gauge,
  Zap,
  Star,
  Globe,
  Shield
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer } from '../../utils/cn';

const Telecommunication = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Ensure page starts from top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const products = [
    {
      icon: Radio,
      title: 'Industrial Grade Rugged Telephones',
      description: 'Guardian Telecom Ltd., Canada - Rugged, safe, reliable and cost-effective telecommunication solutions',
      features: ['Hazardous Area Certified', 'Weather Resistant', 'High Performance', 'Class I Division 1 & 2'],
      image: '/divisions/Telecommunications/radio-communication-and-cell-tower-on-blue-sky-ba-2023-11-27-05-10-39-utc.jpg'
    },
    {
      icon: Shield,
      title: 'Acoustic Hoods',
      description: 'Noise reduction solutions for industrial communication systems',
      features: ['Sound Dampening', 'Weather Protection', 'Durable Construction', 'Easy Installation'],
      image: '/divisions/Telecommunications/close-up-of-network-fiber-cables-2024-09-13-18-48-50-utc.jpg'
    },
    {
      icon: Antenna,
      title: 'PAGA System',
      description: 'Public Address and General Alarm systems for critical communications',
      features: ['Emergency Announcements', 'Zone Control', 'Backup Power', 'Integration Ready'],
      image: '/divisions/Telecommunications/low-angle-shot-of-a-microwave-antenna-with-the-ski-2023-11-27-04-52-30-utc.jpg'
    },
    {
      icon: Monitor,
      title: 'CCTV System',
      description: 'Comprehensive video surveillance and monitoring solutions',
      features: ['HD Video Quality', 'Remote Monitoring', 'Motion Detection', 'Storage Solutions'],
      image: '/divisions/Telecommunications/surveillance-camera-security-cctv-2024-11-21-23-43-16-utc.jpg'
    },
    {
      icon: Shield,
      title: 'Access Control System',
      description: 'Advanced security and access management solutions',
      features: ['Card/Biometric Access', 'Time Attendance', 'Integration Capability', 'Audit Trail'],
      image: '/divisions/Telecommunications/telecommunication-mast-tv-antennas-wireless-techno-2023-11-27-05-21-02-utc.jpg'
    },
    {
      icon: Wifi,
      title: 'Fiber Optic Solutions',
      description: 'Complete fiber optic infrastructure including cables, racks, and accessories',
      features: ['Fiber Optic Cables', 'Telecom Racks', 'Patch Panels', 'Patch Cords'],
      image: '/divisions/Telecommunications/data-storage-corridor-in-data-warehouse-2024-11-03-02-46-57-utc.jpg'
    }
  ];

  const solutions = [
    {
      title: 'Emergency Communication',
      description: 'Reliable communication systems for emergency services and first responders',
      applications: ['Police & Security', 'Fire & Rescue', 'Medical Emergency', 'Disaster Response'],
      image: '/divisions/Telecommunications/telecommunications-engineer-turns-to-the-radio-ant-2023-11-27-05-23-35-utc.jpg'
    },
    {
      title: 'Industrial Communication',
      description: 'Robust communication solutions for industrial environments',
      applications: ['Oil & Gas', 'Mining Operations', 'Manufacturing', 'Utilities'],
      image: '/divisions/Telecommunications/industrial-factory-roof-2024-12-01-01-44-20-utc.jpg'
    },
    {
      title: 'Transportation Networks',
      description: 'Communication systems for transportation infrastructure',
      applications: ['Railway Systems', 'Airport Operations', 'Port Management', 'Highway Networks'],
      image: '/divisions/Telecommunications/bridge-ship-equipment-of-offshore-dp-vessel-2023-11-27-05-25-36-utc.jpg'
    },
    {
      title: 'Enterprise Solutions',
      description: 'Professional communication systems for business operations',
      applications: ['Corporate Networks', 'Campus Communication', 'Retail Operations', 'Hospitality'],
      image: '/divisions/Telecommunications/lightshow-in-dubai-showing-how-the-world-is-evolvi-2024-10-14-16-02-47-utc.jpg'
    }
  ];

  const brands = [
    { name: 'ABB', logo: '/Suppliers logo/ABB.png' },
    { name: 'Schneider Electric', logo: '/Suppliers logo/schneider-logo.png' },
    { name: 'Siemens', logo: '/Suppliers logo/siemens.png' },
    { name: 'Eaton', logo: '/Suppliers logo/Eaton.jpg' },
    { name: 'Honeywell', logo: '/Suppliers logo/Honeywell-Logo.png' },
    { name: 'GE', logo: '/Suppliers logo/GE.png' },
    { name: 'Emerson', logo: '/Suppliers logo/emerson.png' },
    { name: 'Phoenix Contact', logo: '/Suppliers logo/phoenix-contact-logo.png' },
    { name: 'Fluke', logo: '/Suppliers logo/Fluke.png' },
    { name: 'Moxa', logo: '/Suppliers logo/Moxa.png' },
    { name: 'Mean Well', logo: '/Suppliers logo/Mean-Well.png' },
    { name: 'Legrand', logo: '/Suppliers logo/legrand-logo.png' },
    { name: 'Mitsubishi Electric', logo: '/Suppliers logo/mitsubishi-electric.png' }
  ];

  const services = [
    {
      title: 'System Design',
      description: 'Custom communication system design and planning'
    },
    {
      title: 'Installation',
      description: 'Professional installation and commissioning services'
    },
    {
      title: 'Maintenance',
      description: 'Ongoing maintenance and support services'
    },
    {
      title: 'Training',
      description: 'Comprehensive training programs for operators'
    }
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section 
        className="section-padding relative text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.6)), url('/divisions/Telecommunications/communications-tower-2024-10-22-17-32-42-utc.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Geometric Pattern Overlay */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 2px,
                  rgba(59, 130, 246, 0.1) 2px,
                  rgba(59, 130, 246, 0.1) 4px
                ),
                repeating-linear-gradient(
                  -45deg,
                  transparent,
                  transparent 2px,
                  rgba(16, 185, 129, 0.1) 2px,
                  rgba(16, 185, 129, 0.1) 4px
                )
              `,
              backgroundSize: '20px 20px'
            }}
          />
        </div>

        <div className="container-max relative z-10">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            {...fadeInUp}
          >
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Telecommunication Division
              </h1>
              <div className="mb-8">
               
                <p className="text-xl md:text-xl mb-6 leading-relaxed">
                  We offer a full suite of industrial telecommunication products and solutions designed to keep your business connected, efficient, and future ready. From advanced phone systems and intercoms to wireless solutions and PAGA infrastructure, we provide end-to-end support—from product selection and installation to maintenance and technical assistance.
                </p>
               
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" variant="secondary">
                    Get Quote
                    <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                  </Button>
                </Link>
                <Link to="/telecommunicationdivcompany">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-charcoal">
                    Our Partner Company
                  </Button>
                </Link>
              </div>
            </div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="relative mb-6">
                  <img 
                    src="/divisions/Telecommunications/telecommunication-equipment-for-5g-radio-network-2024-12-03-04-12-51-utc.jpg"
                    alt="5G Radio Network Equipment"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                  <div className="absolute inset-0 bg-black/50 rounded-xl" />
                  <Radio className="absolute bottom-4 right-4 w-12 h-12 text-accent" />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">Advanced Communication</h3>
                  <p className="text-lg opacity-90">
                    Providing Comprehensive Industrial Telecommunication Services
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Infrastructure Gallery Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Our Infrastructure
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Explore our state-of-the-art telecommunications infrastructure and cutting-edge technology solutions.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                src: "/divisions/Telecommunications/data-center-with-multiple-rows-of-fully-operationa-2023-11-27-05-16-14-utc.jpg",
                title: "Data Center Operations",
                description: "State-of-the-art data centers with redundant systems"
              },
              {
                src: "/divisions/Telecommunications/fiber-optics-2024-10-21-03-46-49-utc.jpg",
                title: "Fiber Optic Networks",
                description: "High-speed fiber optic infrastructure"
              },
              {
                src: "/divisions/Telecommunications/communications-tower-antenna-tower-against-sunburs-2023-11-27-04-54-22-utc.jpg",
                title: "Communication Towers",
                description: "Strategic tower placement for optimal coverage"
              },
              {
                src: "/divisions/Telecommunications/bundle-of-fibre-optic-cables-2024-06-20-03-08-53-utc.jpg",
                title: "Cable Infrastructure",
                description: "Comprehensive cable management systems"
              },
              {
                src: "/divisions/Telecommunications/surveillance-camera-security-cctv-2024-11-21-23-43-16-utc.jpg",
                title: "Security Systems",
                description: "Advanced surveillance and monitoring"
              },
              {
                src: "/divisions/Telecommunications/telecommunication-tower-broadcasting-signals-for-m-2024-11-15-01-25-40-utc.jpg",
                title: "Broadcasting Solutions",
                description: "Wide-range signal broadcasting capabilities"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -10 }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={item.src}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm opacity-90">{item.description}</p>
                </div>
                <div className="absolute top-4 right-4 w-10 h-10 bg-primary/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Globe className="w-5 h-5 text-white" />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Products / Services
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Comprehensive range of industrial telecommunication products designed for reliability and performance in the most demanding environments.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {products.map((product, index) => {
              const Icon = product.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                >
                  <Card className="h-full hover:scale-105 transition-transform duration-300 overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute top-4 left-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-lg">{product.title}</CardTitle>
                      <CardDescription>{product.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {product.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center text-sm text-slate">
                            <CheckCircle className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="section-padding bg-soft-gray">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Industry Solutions
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Tailored telecommunication solutions for diverse industry requirements.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <Globe className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{solution.title}</CardTitle>
                    <CardDescription>{solution.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {solution.applications.map((app, appIndex) => (
                        <div key={appIndex} className="flex items-center text-sm text-slate">
                          <CheckCircle className="w-3 h-3 text-primary mr-2 flex-shrink-0" />
                          {app}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Brands Global Technology Distributes
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              We partner with leading manufacturers to deliver world-class telecommunication solutions.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {brands.map((brand, index) => (
              <motion.div
                key={index}
                className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                variants={fadeInUp}
              >
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="w-16 h-16 object-contain mx-auto mb-3"
                  onError={(e) => {
                    e.target.src = '/placeholder-logo.svg';
                  }}
                />
                <h3 className="font-semibold text-charcoal mb-1">{brand.name}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      <section 
        className="section-padding text-white relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.7)), url('/divisions/Telecommunications/hallway-with-a-row-of-servers-2023-11-27-04-52-33-utc.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Overlay Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 2px,
                  rgba(255, 255, 255, 0.1) 2px,
                  rgba(255, 255, 255, 0.1) 4px
                )
              `,
              backgroundSize: '20px 20px'
            }}
          />
        </div>

        <div className="container-max relative z-10">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Services
            </h2>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Comprehensive support services to ensure optimal performance of your telecommunication systems.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="text-center group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-primary/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/30 transition-colors duration-300">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="opacity-90">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-12 lg:py-16 relative overflow-hidden">        
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-5 left-10 w-20 h-20 bg-teal-400/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-10 right-10 w-24 h-24 bg-accent/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
        </div>
        
        <div className="container-max relative z-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center min-h-[400px]">
            {/* Left Side - Polygon Design Pattern */}
            <div className="relative h-full lg:col-span-2">
              {/* Polygon Shape with Hero-style Design */}
              <div 
                className="absolute inset-0 bg-gradient-to-br from-teal-400 via-teal-500 to-teal-600 z-5 w-[1000px] h-full"
                style={{
                  clipPath: 'polygon(0 0, 100% 0, 70% 100%, 0% 100%)'
                }}
              >
                {/* Clean Geometric Pattern */}
                <div 
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: `
                      repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 20px,
                        rgba(255,255,255,0.3) 20px,
                        rgba(255,255,255,0.3) 22px,
                        transparent 22px,
                        transparent 40px,
                        rgba(255,255,255,0.2) 40px,
                        rgba(255,255,255,0.2) 42px
                      ),
                      repeating-linear-gradient(
                        -45deg,
                        transparent,
                        transparent 30px,
                        rgba(255,255,255,0.2) 30px,
                        rgba(255,255,255,0.2) 32px,
                        transparent 32px,
                        transparent 60px,
                        rgba(255,255,255,0.1) 60px,
                        rgba(255,255,255,0.1) 62px
                      )
                    `,
                    backgroundSize: '80px 80px, 100px 100px'
                  }}
                />
                {/* Additional Geometric Lines */}
                <div 
                  className="absolute inset-0 opacity-8"
                  style={{
                    backgroundImage: `
                      repeating-linear-gradient(
                        0deg,
                        transparent,
                        transparent 50px,
                        rgba(255,255,255,0.15) 50px,
                        rgba(255,255,255,0.15) 51px
                      ),
                      repeating-linear-gradient(
                        90deg,
                        transparent,
                        transparent 50px,
                        rgba(255,255,255,0.15) 50px,
                        rgba(255,255,255,0.15) 51px
                      )
                    `
                  }}
                />
              </div>
              
              {/* Content over polygon */}
              <motion.div
                className="relative z-20 text-white p-6 lg:p-8 h-full flex flex-col justify-center"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >                
                <motion.h2 
                  className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  Ready to Enhance Your Communication?
                </motion.h2>
                
                <motion.p 
                  className="text-base lg:text-lg mb-6 text-gray-100 leading-relaxed font-bold"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  Get in touch with our experts to discuss your engineering needs
                  <span className="text-charcoal"><br />
                    Contact our telecommunication experts to discuss your specific requirements and find the perfect solution.
                  </span>
                </motion.p>
                
                <motion.div 
                  className="flex flex-col sm:flex-row gap-3"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Link to="/contact">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button size="lg" className="bg-black text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl">
                        Get a Free Quote
                        <motion.div
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                        </motion.div>
                      </Button>
                    </motion.div>
                  </Link>
                  <Link to="/industries">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button variant="outline" size="lg" className="border-white/50 text-white hover:bg-white/10 hover:border-white backdrop-blur-sm transition-all duration-300">
                        view industries
                      </Button>
                    </motion.div>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
            
          </div>
        </div>
      </section>
    </div>
  );
};

export default Telecommunication;